import { useEffect, useRef, useMemo, useCallback } from 'react';
import logger from 'ws-scripts/modules/logger';
import { MARKER_ICONS } from '../constants/mapConstants';
import { createMarkers, fitMapBounds } from '../utils/mapUtils';
import useCustomOverlay from './useCustomOverlay';
import useMapDragState from './useMapDragState';

/**
 * Build a Google Maps icon object from a MARKER_ICONS entry.
 * @param {{ url: string, size: number[], anchor: number[] }} iconConfig
 * @returns {Object} Google Maps icon descriptor
 */
const buildGoogleIcon = (iconConfig) => ({
	url: iconConfig.url,
	scaledSize: new window.google.maps.Size(...iconConfig.size),
	anchor: new window.google.maps.Point(...iconConfig.anchor)
});

/**
 * Hook to manage Google Maps markers for a set of locations.
 * Clears and recreates markers whenever the locations array changes.
 *
 * @param {google.maps.Map | null} map - The Google Maps instance (null until initialised)
 * @param {Array} locations - Array of location/tile objects
 * @param {boolean} isLoaded - Whether the Google Maps script has loaded
 * @param {number} hoveredTileIndex - Index of the currently hovered tile (for marker highlighting)
 */
const useMapMarkers = (map, locations, isLoaded, hoveredTileIndex) => {
	const markersRef = useRef([]);
	const overlayRef = useRef(null);
	const hoveredTileIndexRef = useRef(hoveredTileIndex);
	hoveredTileIndexRef.current = hoveredTileIndex;

	// Delegate drag detection and map-click-to-close to a dedicated hook.
	const onMapClick = useCallback(() => overlayRef.current?.close(), []);
	const isDragging = useMapDragState(map, onMapClick);

	// Use custom overlay hook for overlay management
	const { customOverlayRef, handleMarkerClick, portalContainer, tileData } =
		useCustomOverlay({
			map,
			isDragging,
			locations
		});

	// Keep the stable ref in sync with the overlay instance so that
	// onMapClick always targets the latest overlay without re-binding.
	overlayRef.current = customOverlayRef.current;

	// Keep a ref to handleMarkerClick so the markers effect can use
	// the latest version without having it as a dependency (prevents drag snap-back)
	const handleMarkerClickRef = useRef(handleMarkerClick);
	handleMarkerClickRef.current = handleMarkerClick;

	const icons = useMemo(() => {
		if (!isLoaded || !window.google?.maps) {
			return null;
		}
		return {
			default: buildGoogleIcon(MARKER_ICONS.default),
			active: buildGoogleIcon(MARKER_ICONS.active)
		};
	}, [isLoaded]);

	useEffect(() => {
		if (!map || !window.google) {
			return undefined;
		}

		// Clear existing markers
		markersRef.current.forEach((marker) => marker.setMap(null));
		markersRef.current = [];

		// Create new markers and fit the map viewport
		markersRef.current = createMarkers(
			map,
			locations,
			icons,
			(...args) => handleMarkerClickRef.current(...args),
			hoveredTileIndexRef
		);
		fitMapBounds(map, markersRef.current);

		// Cleanup markers on unmount or before next run
		return () => {
			// Remove all markers
			markersRef.current.forEach((marker) => {
				try {
					marker.setMap(null);
				} catch (error) {
					// Log marker removal errors
					logger.error('Error removing marker:', error);
				}
			});
			markersRef.current = [];
		};
	}, [map, locations, icons]);

	useEffect(() => {
		// Bring pins into view when hovering over a tile
		if (hoveredTileIndex !== -1) {
			fitMapBounds(map, markersRef.current);
		}
	}, [hoveredTileIndex]);

	/**
	 * Update marker icon when hoveredTileIndex changes
	 */
	useEffect(() => {
		if (markersRef.current.length === 0) {
			return;
		}

		// Update all marker icons based on hoveredTileIndex
		markersRef.current.forEach((marker, index) => {
			if (index === hoveredTileIndex) {
				marker.setIcon(icons.active);
			} else {
				marker.setIcon(icons.default);
			}
		});
	}, [hoveredTileIndex, icons]);

	return {
		customOverlayRef,
		portalContainer,
		tileData
	};
};

export default useMapMarkers;
